import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

export interface PropertyContract {
  id: string;
  property_id: string;
  organization_id?: string;
  operation_start_date: string;
  cleaning_rate: number;
  administration_rate: number;
  administration_commission: number;
  promotional_commission?: number;
  promotion_end_date?: string;
  status: 'active' | 'cancelled';
  admin_type: 'Propio' | 'Renta' | 'Administración';
  notes?: string;
  rental_payment_day?: number;
  rental_monthly_amount?: number;
  rental_iva_percentage?: number;
  created_at: string;
  updated_at: string;
}

export const useContracts = () => {
  return useQuery({
    queryKey: ["contracts"],
    queryFn: async (): Promise<PropertyContract[]> => {
      const { data, error } = await supabase
        .from("property_contracts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []) as PropertyContract[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (contractData: Omit<PropertyContract, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("property_contracts")
        .insert([contractData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Actualizar operation_started_at en la propiedad
      const { error: updateError } = await supabase
        .from("properties")
        .update({ operation_started_at: contractData.operation_start_date })
        .eq("id", contractData.property_id);

      if (updateError) {
        console.error("Error updating property operation_started_at:", updateError);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast({
        title: "Contrato creado",
        description: "El contrato se ha creado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error al crear contrato",
        description: error.message || "Ha ocurrido un error inesperado.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...contractData }: Partial<PropertyContract> & { id: string }) => {
      const { data, error } = await supabase
        .from("property_contracts")
        .update(contractData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Si se actualiza la fecha de inicio, actualizar también en properties
      if (contractData.operation_start_date && contractData.property_id) {
        const { error: updateError } = await supabase
          .from("properties")
          .update({ operation_started_at: contractData.operation_start_date })
          .eq("id", contractData.property_id);

        if (updateError) {
          console.error("Error updating property operation_started_at:", updateError);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast({
        title: "Contrato actualizado",
        description: "El contrato se ha actualizado exitosamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error al actualizar contrato",
        description: error.message || "Ha ocurrido un error inesperado.",
        variant: "destructive",
      });
    },
  });
};